<template>
  <div>
    <div class="m-subheader">
      <div class="d-flex align-items-center">
        <div class="mr-auto">
          <h3 class="m-subheader__title m-subheader__title--separator">
            {{ $t("message.employees.administration.profileIntegration.title") }}
          </h3>
        </div>
      </div>
    </div>
    <div class="w-full flex justify-center">
      <div class="main font-normal relative">
        <div>
          <div class="heading p-6 flex items-center">
            <h2 class="text-xl text-zt-primary-200">
              {{ $t("message.employees.administration.profileIntegration.subTitle") }}
            </h2>
          </div>
          <div v-if="shownAlert" class="w-full mb-6">
            <zt-alert :color="alert.color" :close-icon="true" @afterClosed="shownAlert = false" class="p-4 m-6">
              <div slot="custom-content">
                <h4 class="alert-title font-bold mb-2">{{ alert.title }}</h4>
                <ul>
                  <li v-for="(detail, index) in alert.details" :key="index" class="alert-detail">
                    {{ detail }}
                    <a
                      v-if="alert.link && index === alert.details.length - 1"
                      href="#" class="font-bold text-zt-dark-300"
                      @click.prevent="downloadFile(false)"
                      data-vs-link="errors"
                    >
                      {{ $t("message.employees.administration.message.failed.link") }}
                    </a>
                  </li>
                  <li v-if="alert.duplicates" class="alert-detail">
                    {{ alert.duplicates }}
                    <a
                      href="#"
                      class="font-bold text-zt-dark-300"
                      @click.prevent="downloadFile(true)"
                      data-vs-link="duplicates"
                    >
                      {{ $t("message.employees.administration.message.duplicates.link") }}
                    </a>
                  </li>
                </ul>
              </div>
            </zt-alert>
          </div>
          <div class="w-full p-6 mb-6">
            <h3 class="font-bold mb-2">
              {{ $t("message.employees.administration.profileIntegration.step.question") }}
            </h3>
            <p class="text-zt-dark-300">
              <span>{{ $t("message.employees.administration.profileIntegration.step.description") }}</span>
              <a :href="toExample" class="text-zt-primary-100" data-vs-link="example">
                {{ $t("message.employees.administration.profileIntegration.step.link") }}
              </a>
            </p>
          </div>
          <div class="w-full p-6 mb-6">
            <h3 class="font-bold mb-6">
              {{ $t("message.employees.administration.profileIntegration.step1.question") }}
            </h3>
            <div class="button-group mb-12 flex items-center">
              <button
                  class="bg-zt-primary-200 p-2 flex justify-center items-center rounded-tl rounded-bl rounded-tr-none rounded-br-none"
                  :class="{ selected: assignProfile }"
                  @click="assignProfile = true"
                  data-vs-button="assignProfile"
              >
                {{ $t("message.employees.administration.profileIntegration.step1.assign") }}
              </button>
              <button
                  class="bg-zt-primary-200 p-2 flex justify-center items-center rounded-tr rounded-br rounded-tl-none rounded-bl-none"
                  :class="{ selected: !assignProfile }"
                  @click="assignProfile = false"
                  data-vs-button="unassignProfile"
              >
                {{ $t("message.employees.administration.profileIntegration.step1.unassign") }}
              </button>
            </div>
            <div class="w-full flex">
              <div class="w-3/4">
                <multiselect
                  v-model="profileSelected"
                  placeholder="Seleccione un perfil"
                  :disabled="!profileList.length"
                  :options="profileList"
                  :searchable="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :value="profileSelected"
                  label="nombre"
                >
                  <template slot="noResult">
                <span class="multiselect__single">
                  {{ $t("message.employees.administration.profileIntegration.step1.noResult") }}
                </span>
                  </template>
                  <template slot="option" slot-scope="{ option }">
                    <span>{{ option.nombre }}</span>
                  </template>
                </multiselect>
              </div>
              <div
                v-if="profileList.length"
                class="w-1/4 ml-2 flex justify-start items-center"
              >
                <button class="button-add flex justify-start items-center" @click="addProfile" data-vs-button="addProfile">
                  <div class="la la-plus-circle add-icon"></div>
                  <div class="button-add ml-1">
                    {{ $t("message.employees.administration.profileIntegration.step1.add") }}
                  </div>
                </button>
              </div>
            </div>
            <div v-if="profileSelectedList.length" class="mt-6">
              <transition-group name="slide" mode="out-in">
                <zt-chip
                  v-for="(profile, index) in profileSelectedList"
                  closable
                  :key="profile.nombre"
                  @click:close="removeProfile(index)"
                  class="inline-block align-middle"
                  data-vs-chip="profileChip"
                >
                  {{ profile.nombre }}
                </zt-chip>
              </transition-group>
            </div>
          </div>
          <div class="w-full p-6 mb-6">
            <h3 class="font-bold mb-12">
              {{ $t("message.employees.administration.profileIntegration.step2.question") }}
            </h3>
            <zt-input-file
              ref="ztInputFile"
              @change="selectFile"
              outlined
              :label="$t('message.employees.administration.profileIntegration.step2.selectFile')"
              append-outer-icon="paperclip-2"
              filetypes=".csv"
              accept=".csv"
              :class=" { error: error }"
              data-vs-input="inputFile"
            >
              <template slot="message">
                <span v-if="error" class="text-zt-danger text-zt-md">{{ error }}</span>
              </template>
            </zt-input-file>
          </div>
          <div class="w-full p-6 flex justify-end items-center">
            <zt-button
              color="secondary"
              :disabled="!isSaveAvailable"
              :loader="isSaving"
              rounded @click="save"
              data-vs-button="save"
            >{{ buttonText }}</zt-button>
          </div>
        </div>
        <div v-if="isLoading" class="absolute w-full top-0 left-0 h-full">
          <vue-element-loading
            :active="isLoading"
            spinner="spinner"
            duration="1.5"
            color="#4f4f4f"
            size="50"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import VueElementLoading from 'vue-element-loading'
import { findIndex, has, isEmpty }  from 'lodash'
import UserManagement from '../../../apiClient/UserManagement'
export default {
  name: 'IntegrationProfile',
  components: { Multiselect, VueElementLoading },
  data() {
    return {
      assignProfile: true,
      cacheProfileList: [],
      error: '',
      file: '',
      isLoading: true,
      isSaving: false,
      processFailedFileCSV: null,
      processMessages: {},
      profileList: [],
      profileSelected: null,
      profileSelectedList: [],
      shownAlert: false,
      toExample: `${location.origin}/assets/media/listadoCuil.csv`
    }
  },
  watch: {
    file(val) {
      if (!val) this.error = ''
    }
  },
  computed: {
    buttonText() {
      return this.assignProfile
        ? this.$t("message.employees.administration.profileIntegration.toSaveAssign")
        : this.$t("message.employees.administration.profileIntegration.toSaveUnassign")
    },
    isSaveAvailable() {
      return this.file && !this.error && this.profileSelectedList.length
    },
    alert() {
      if (!isEmpty(this.processMessages)) {
        const deletedExists = has(this.processMessages, 'deleted')
        const duplicatesExists = has(this.processMessages, 'duplicates')
        const failedExists = has(this.processMessages, 'failed')
        const inactiveExists = has(this.processMessages, 'inactive')
        const insertedExists = has(this.processMessages, 'inserted')
        const errorExists = has(this.processMessages, 'error')
        const total = this.processMessages.total
        let color = ''
        let quantity = 0
        if (errorExists && !duplicatesExists && !failedExists && !inactiveExists) color = 'danger'
        if (duplicatesExists || failedExists || inactiveExists) color = 'info'
        if ((insertedExists || deletedExists) && !duplicatesExists && !failedExists && !inactiveExists) color = 'success'
        if (insertedExists) quantity = this.processMessages.inserted
        if (deletedExists) quantity = this.processMessages.deleted
        return {
          color,
          link: color === 'info' && (failedExists || inactiveExists),
          title: this.$t(`message.employees.administration.message.${color === 'danger' ? 'error' : color}`),
          details: [
            ...(insertedExists || deletedExists ? [
              this.$t(
                'message.employees.administration.message.inserted.label',
                {
                  quantity,
                  total
                }
              )
            ]: []),
            ...(inactiveExists ? [
              this.$t(
                'message.employees.administration.message.inactive.label',
                {
                  quantity: this.processMessages.inactive.length,
                  total
                }
              )
            ]: []),
            ...(failedExists ? [
              this.$t(
                'message.employees.administration.message.failed.label',
                {
                  quantity: this.processMessages.failed.length,
                  total
                }
              )
            ]: []),
            ...(errorExists ? [
              this.processMessages.error
            ]: [])
          ],
          ...(duplicatesExists ?
            {
              duplicates: this.$t(
                'message.employees.administration.message.duplicates.label',
                {
                  quantity: this.processMessages.duplicates.length,
                  total
                }
              )
            }: {})
        }
      }
    },
    isValidFile(){
      if (!this.file) return false
      return this.file.name.split('.').pop() === 'csv'
        && (this.file.type === 'text/csv' || this.file.type === 'application/vnd.ms-excel' || this.file.type === 'text/plain')
    }
  },
  async created() {
    this.userManagementApi = new UserManagement()
    try {
      const { empresa: { id } } = JSON.parse(localStorage.getItem('user'))
      const { data } = await this.userManagementApi.getProfileCompany(id, { profileTypes: '[4]' })
      this.profileList = data
      this.cacheProfileList = [...this.profileList]
      this.isLoading = false
    } catch (e) {
      this.profileList = []
      this.isLoading = false
    }
  },
  methods: {
    addProfile() {
      const { profileSelected } = this
      if (!profileSelected) return
      const index = findIndex(this.profileList, { id: profileSelected.id })
      this.profileSelectedList.push(profileSelected)
      this.profileList.splice(index, 1)
      this.profileSelected = null
    },
    buildCSV(duplicate = false) {
      const duplicates = has(this.processMessages, 'duplicates')
      const failed = has(this.processMessages, 'failed')
      const inactive = has(this.processMessages, 'inactive')
      const rows = (arr, observation = '') => arr.map( uuid => [uuid, observation].join(','))
      const csvFile = [
        ...(!duplicate && failed ? rows(this.processMessages.failed, this.$t('message.employees.administration.message.failed.observation')) : []),
        ...(!duplicate && inactive ? rows(this.processMessages.inactive, this.$t('message.employees.administration.message.inactive.observation')) : []),
        ...(duplicate && duplicates ? rows(this.processMessages.duplicates, this.$t('message.employees.administration.message.duplicates.observation')) : [])
      ]
      return new Blob([csvFile.join('\n')], { type: 'text/csv;charset=utf-8;' })
    },
    clear() {
      this.file = null
      this.profileSelectedList = []
      this.profileList = [...this.cacheProfileList]
      this.$refs.ztInputFile.cleanValue()
    },
    async downloadFile(duplicate = false) {
      if (this.processFailedFileCSV) return
      this.processFailedFileCSV = this.buildCSV(duplicate)
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.style.display = 'none'
      a.setAttribute('href', URL.createObjectURL(this.processFailedFileCSV))
      a.setAttribute('download', 'listadoCuil.csv')
      a.click()
      this.processFailedFileCSV = null
    },
    removeProfile(index) {
      if (this.isSaving) return
      this.profileList.push(this.profileSelectedList[index])
      this.profileSelectedList.splice(index, 1)
    },
    selectFile(value) {
      this.file = value
      if (this.file && !this.isValidFile) {
        this.error = this.$t("message.employees.administration.profileIntegration.validFile")
      }
    },
    async save() {
      if (this.isSaving || this.error) return
      this.isSaving = true
      if ( this.shownAlert ) this.shownAlert = false
      try {
        const params = {
          profileTypesId: this.profileSelectedList.map(profile => profile.id),
          cuilUsers: this.file
        }
        if (this.assignProfile) {
          const { data: { data } } = await this.userManagementApi.massiveAddIntegrationProfile(params)
          this.processMessages = data
        } else {
          const { data: { data } } = await this.userManagementApi.massiveRemoveIntegrationProfile(params)
          this.processMessages = data
        }
        this.shownAlert = true
        this.isSaving = false
        this.clear()
      } catch (e) {
        if (e.response.status === 400) {
          const code = e.response.data.codes[0].code
          this.processMessages = {
            error: this.$t(`message.employees.administration.error.${code}`)
          }
        }
        this.shownAlert = true
        this.isSaving = false
        this.clear()
      }
    }
  }
};
</script>
<style scoped lang="scss">
.heading {
  border-bottom: 1px solid #ebedf2;
  h2 {
    font-size: 24px;
  }
}
.main {
  width: 720px;
  height: auto;
  box-shadow: 0 1px 15px 1px rgba(69, 65, 78, .08);
  background: #fff;
  overflow: hidden;
  h3 {
    font-size: 1.25rem;
    line-height: 1;
    color: #4F4F4F;
  }
  p {
    font-size: 1.125rem;
  }
  .absolute {
    position: absolute!important;
  }
}
.button-group {
  width: 65%;
  > button {
    width: 50%;
    background: #E0E0E0;
    transition: all 300ms cubic-bezier(0.550, 0.085, 0.680, 0.530); /* easeInQuad */
    transition-timing-function: cubic-bezier(0.550, 0.085, 0.680, 0.530); /* easeInQuad */
  }
  button.selected {
    background: #4F41A3;
    color: #FFF;
  }
}
.button-add {
  &:hover {
    color: #4F41A3;
  }
  .add-icon {
    font-size: 1.75rem;
  }
}
::v-deep {
  .error {
    .zt-input-main {
      fieldset {
        border: solid 1px #F16B6C !important;
      }
      label {
        color: #F16B6C!important;
      }
    }
  }
  .alert-title {
    font-size: 1.25rem;
  }
  .alert-detail {
    font-size: 1.125rem;
  }
}
</style>